// Verlof V2 — 9-status state-machine voor LeaveRequest (verlofmodule-spec §5.2).
//
// Bron-hiërarchie:
//   - 9-stap workflow met 4-ogen: spec §5.2 + apps/api/src/routes/leave-v2.ts.
//   - V1 statussen (AANGEVRAAGD/GOEDGEKEURD/AFGEWEZEN) worden hier NIET
//     opgenomen — die zijn legacy en worden door de oude /leave/* routes
//     gehandeld.
//
// Workflow:
//   CONCEPT → INGEDIEND → GOEDGEKEURD_TEAMLEIDER → GECLASSIFICEERD → DEFINITIEF
//                       ↘ GECLASSIFICEERD         (HR-doorklas, TL-stap overgeslagen)
//                       ↘ AFGEKEURD_TEAMLEIDER → INGEDIEND  (HR overrule)
//                                              ↘ AFGEKEURD  (HR finaal)
//                       ↘ AFGEKEURD             (HR direct na GOEDGEKEURD_TL)
//   * → INGETROKKEN  (eigenaar of backoffice; ná DEFINITIEF gaat het via
//                     intrekkingsRoute-flag, geen status-overgang).

import { WorkflowEngine, type WorkflowDefinition } from '../workflow-engine'

/** Mirror van Prisma LeaveStatus enum (V2 statussen) als string-literal union. */
export type VerlofStatusValue =
  | 'CONCEPT'
  | 'INGEDIEND'
  | 'GOEDGEKEURD_TEAMLEIDER'
  | 'AFGEKEURD_TEAMLEIDER'
  | 'GECLASSIFICEERD'
  | 'DEFINITIEF'
  | 'OVERRULED'
  | 'AFGEKEURD'
  | 'INGETROKKEN'
  // Procedure-correctie Deel 4 — TL stelt aanvullende vraag (terug bij medewerker).
  | 'AANVULLENDE_VRAAG'

/**
 * Verlofworkflow-definitie.
 *
 * `rolesAllowed` is informatief — server-side wordt rol al door
 * `requirePermission('VERLOF', 'canApprove'|'canEdit')` gechecked. De engine
 * gebruikt dit voor `validTransitions(from, role)` zodat UI-knopjes per rol
 * gefilterd kunnen worden.
 */
export const verlofWorkflow: WorkflowDefinition<VerlofStatusValue> = {
  name: 'verlof',
  initial: 'CONCEPT',
  states: [
    'CONCEPT',
    'INGEDIEND',
    'GOEDGEKEURD_TEAMLEIDER',
    'AFGEKEURD_TEAMLEIDER',
    'GECLASSIFICEERD',
    'DEFINITIEF',
    'OVERRULED',
    'AFGEKEURD',
    'INGETROKKEN',
    'AANVULLENDE_VRAAG',
  ],
  transitions: [
    // 1. Concept → Indienen (medewerker)
    {
      from: 'CONCEPT',
      to: 'INGEDIEND',
      label: 'Indienen',
      rolesAllowed: ['MONTEUR', 'TEAMLEIDER', 'PLANNER', 'HR', 'MANAGER', 'ADMIN'],
    },
    // 2. TL beoordeelt
    {
      from: 'INGEDIEND',
      to: 'GOEDGEKEURD_TEAMLEIDER',
      label: 'TL goedkeuren',
      rolesAllowed: ['TEAMLEIDER', 'HR', 'ADMIN'],
    },
    {
      from: 'INGEDIEND',
      to: 'AFGEKEURD_TEAMLEIDER',
      label: 'TL afkeuren',
      rolesAllowed: ['TEAMLEIDER', 'HR', 'ADMIN'],
    },
    // 2b. Procedure-correctie Deel 4 — TL stelt aanvullende vraag (geen oordeel);
    // de aanvraag pendelt naar de medewerker en weer terug.
    {
      from: 'INGEDIEND',
      to: 'AANVULLENDE_VRAAG',
      label: 'TL aanvullende vraag',
      rolesAllowed: ['TEAMLEIDER', 'HR', 'ADMIN'],
    },
    {
      from: 'AANVULLENDE_VRAAG',
      to: 'INGEDIEND',
      label: 'Medewerker beantwoordt vraag',
      rolesAllowed: ['MONTEUR', 'TEAMLEIDER', 'PLANNER', 'HR', 'MANAGER', 'ADMIN'],
    },
    // 3. HR overrule TL-afkeur (terug naar INGEDIEND)
    {
      from: 'AFGEKEURD_TEAMLEIDER',
      to: 'INGEDIEND',
      label: 'HR overrule (terug naar TL)',
      rolesAllowed: ['HR', 'ADMIN'],
    },
    // 4. HR finaal afkeur — vanuit GOEDGEKEURD_TL of AFGEKEURD_TL
    {
      from: 'GOEDGEKEURD_TEAMLEIDER',
      to: 'AFGEKEURD',
      label: 'HR finaal afkeuren',
      rolesAllowed: ['HR', 'ADMIN'],
    },
    {
      from: 'AFGEKEURD_TEAMLEIDER',
      to: 'AFGEKEURD',
      label: 'HR finaal afkeuren',
      rolesAllowed: ['HR', 'ADMIN'],
    },
    // 5. HR classificeert + boekt af op emmers
    {
      from: 'GOEDGEKEURD_TEAMLEIDER',
      to: 'GECLASSIFICEERD',
      label: 'HR classificeren',
      rolesAllowed: ['HR', 'ADMIN'],
    },
    // 5b. HR-doorklas: in een kleine organisatie is er niet altijd een (andere)
    // teamleider. HR mag een INGEDIENDE aanvraag direct classificeren; de
    // TL-route blijft de standaard. Het endpoint audit dit expliciet als
    // GECLASSIFICEERD_ZONDER_TL en de 4-ogen-guard op de aanvrager (regel 18)
    // blijft onverkort gelden.
    {
      from: 'INGEDIEND',
      to: 'GECLASSIFICEERD',
      label: 'HR classificeren (TL-stap overslaan)',
      rolesAllowed: ['HR', 'ADMIN'],
    },
    // 6. Definitief boeken (na classificatie)
    {
      from: 'GECLASSIFICEERD',
      to: 'DEFINITIEF',
      label: 'Definitief boeken',
      rolesAllowed: ['HR', 'ADMIN'],
    },
    // 7. Intrekken — vanuit alle non-terminal niet-afgewezen statussen
    {
      from: 'CONCEPT',
      to: 'INGETROKKEN',
      label: 'Intrekken',
    },
    {
      from: 'INGEDIEND',
      to: 'INGETROKKEN',
      label: 'Intrekken',
    },
    {
      from: 'GOEDGEKEURD_TEAMLEIDER',
      to: 'INGETROKKEN',
      label: 'Intrekken',
    },
    {
      from: 'GECLASSIFICEERD',
      to: 'INGETROKKEN',
      label: 'Intrekken',
    },
    // OVERRULED is een legacy-state die in spec voorkomt maar in de huidige
    // implementatie via terug-naar-INGEDIEND wordt gemodelleerd. We laten 'm
    // bestaan in de states-lijst voor compat, zonder transitions ervan.
  ],
}

/** Engine-instance voor verlof — direct importeerbaar in API-routes. */
export const verlofEngine = new WorkflowEngine(verlofWorkflow)
